import React, { useEffect, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { errorText } from "../managment/enrollmentRequests/helpers";
import "./tableroPromociones.css";

/*
	Resumen de promociones del Tablero de Ventas (Esteban, 2026-10-30): lo cobrado con promos de adelanto, lo regalado y cuántas se
	aplicaron en el mes, por promoción y por equipo (con sus asesoras). Lo calcula el servidor según quién mira (asesora: su equipo;
	supervisión y dirección: todo) y solo cuenta lo Aplicado. Quien no puede ver montos no recibe nada y el panel no sale.
	Con monedas mezcladas el servidor manda un bloque por moneda y aquí también salen separados.
*/

/** Promos En Camino que no se completaron en el mes: cuántas y por cuánto era la meta (docs/CONTRATO_PROMO_EN_CAMINO.md §1). */
interface Fallidas { count: number; target: number }
interface Fila { name: string; cobrado: number; regalado: number; aplicadas: number; fallidas?: Fallidas }
interface Asesora extends Fila { userId?: number | null; team?: string }
interface Equipo { team: string; cobrado: number; regalado: number; aplicadas: number; fallidas?: Fallidas; porAsesora: Asesora[] }
interface Bloque {
	currencySymbol: string | null;
	cobrado: number;
	regalado: number;
	aplicadas: number;
	fallidas?: Fallidas;
	regaladoPorCobrado: number | null;
	porPromo: (Fila & { promotionId?: number })[];
	porEquipo: Equipo[];
	porAsesora: Asesora[];
}
interface Resumen extends Partial<Bloque> {
	month: string;
	visible: boolean;
	scope: { all: boolean; team: string | null; scope?: string; campusIds?: number[] };
	porMoneda?: Bloque[];
}

const dinero = (sym: string | null | undefined, n: number | null | undefined): string => {
	const s = sym || "";
	return (/^[A-Za-z]/.test(s) ? s + " " : s) + (Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const entero = (n: number) => (Number(n) || 0).toLocaleString("es-MX");

const Barra: React.FC<{ valor: number; maximo: number }> = ({ valor, maximo }) => (
	<span className="tp-barra" aria-hidden="true"><i style={{ width: Math.max(2, Math.round((valor / (maximo || 1)) * 100)) + "%" }} /></span>
);

/** Equipo sin cifra propia de fallidas: se suma de sus asesoras. */
const fallidasDe = (e: Equipo): Fallidas | undefined => e.fallidas || (e.porAsesora.some((a) => a.fallidas)
	? { count: e.porAsesora.reduce((n, a) => n + (a.fallidas?.count || 0), 0), target: e.porAsesora.reduce((n, a) => n + (a.fallidas?.target || 0), 0) } : undefined);

const Cabecera: React.FC<{ primera: string; conFallidas?: boolean }> = ({ primera, conFallidas }) => (
	<div className={"tp-fila tp-cabecera" + (conFallidas ? " tp-f5" : "")}><span>{primera}</span><span className="tp-num">Aplic.</span><span className="tp-num">Cobrado</span><span className="tp-num">Regalado</span>{conFallidas ? <span className="tp-num">Fallidas</span> : null}</div>
);
/** Celda de Fallidas: cuántas y, debajo, la meta que se perdió. */
const CeldaFallidas: React.FC<{ f?: Fallidas; sym: string | null }> = ({ f, sym }) => (
	<span className="tp-num tp-falla">{f && f.count > 0 ? <><b>{entero(f.count)}</b><small>{dinero(sym, f.target)}</small></> : <span className="tp-nada">0</span>}</span>
);

const PorEquipo: React.FC<{ b: Bloque }> = ({ b }) => {
	const [abierto, setAbierto] = useState<string | null>(null);
	const sym = b.currencySymbol;
	const maximo = b.porEquipo.reduce((m, e) => Math.max(m, e.cobrado), 0);
	const conF = b.porEquipo.some((e) => fallidasDe(e));
	return (
		<div className="tp-card">
			<h5>Por Equipo</h5>
			<Cabecera primera="Equipo" conFallidas={conF} />
			<ul className="tp-lista">
				{b.porEquipo.map((e) => (
					<li key={"eq" + e.team}>
						<button type="button" className={"tp-fila" + (conF ? " tp-f5" : "")} aria-expanded={abierto === e.team} onClick={() => setAbierto(abierto === e.team ? null : e.team)}>
							<span className="tp-nombre">{e.team}</span><span className="tp-num">{entero(e.aplicadas)}</span>
							<span className="tp-num">{dinero(sym, e.cobrado)}</span><span className="tp-num">{dinero(sym, e.regalado)}</span>
							{conF ? <CeldaFallidas f={fallidasDe(e)} sym={sym} /> : null}
							<Barra valor={e.cobrado} maximo={maximo} />
						</button>
						{abierto === e.team ? (
							<ul className="tp-sub">
								{e.porAsesora.map((a, i) => (
									<li key={"ea" + e.team + i} className={"tp-fila" + (conF ? " tp-f5" : "")}>
										<span className="tp-nombre">{a.name}</span><span className="tp-num">{entero(a.aplicadas)}</span>
										<span className="tp-num">{dinero(sym, a.cobrado)}</span><span className="tp-num">{dinero(sym, a.regalado)}</span>
										{conF ? <CeldaFallidas f={a.fallidas} sym={sym} /> : null}
									</li>
								))}
							</ul>
						) : null}
					</li>
				))}
			</ul>
		</div>
	);
};

const PanelBloque: React.FC<{ b: Bloque; todo: boolean; equipo: string | null; conMoneda: boolean }> = ({ b, todo, equipo, conMoneda }) => {
	const sym = b.currencySymbol;
	const unoPor = (/^[A-Za-z]/.test(sym || "") ? (sym || "") + " 1" : (sym || "") + "1");
	const maxPromo = b.porPromo.reduce((m, p) => Math.max(m, p.cobrado), 0);
	const conFA = b.porAsesora.some((a) => a.fallidas);
	return (
		<>
			{conMoneda || equipo ? (
				<div className="tp-cab">
					{conMoneda ? <span className="tp-moneda">{sym}</span> : null}
					{equipo ? <span className="tp-moneda">Equipo {equipo}</span> : null}
				</div>
			) : null}
			<div className="tp-kpis">
				<div className="tp-kpi"><span>Cobrado</span><b>{dinero(sym, b.cobrado)}</b></div>
				<div className="tp-kpi"><span>Regalado</span><b>{dinero(sym, b.regalado)}</b></div>
				<div className="tp-kpi"><span>Aplicadas</span><b>{entero(b.aplicadas)}</b></div>
				<div className="tp-kpi"><span>{"Regalado por Cada " + unoPor + " Cobrado"}</span><b>{b.regaladoPorCobrado == null ? "-" : dinero(sym, b.regaladoPorCobrado)}</b></div>
				{b.fallidas ? <div className="tp-kpi"><span>Fallidas</span><b>{entero(b.fallidas.count)}</b>{b.fallidas.count > 0 ? <small>{dinero(sym, b.fallidas.target)}</small> : null}</div> : null}
			</div>
			{b.aplicadas === 0 && !(b.fallidas && b.fallidas.count > 0) ? <div className="tp-vacio">Sin Promociones Aplicadas</div> : (
				<div className="tp-grid">
					<div className="tp-card">
						<h5>Por Promoción</h5>
						<Cabecera primera="Promoción" />
						<ul className="tp-lista">
							{b.porPromo.map((p) => (
								<li key={"pp" + (p.promotionId || p.name)} className="tp-fila">
									<span className="tp-nombre">{p.name}</span><span className="tp-num">{entero(p.aplicadas)}</span>
									<span className="tp-num">{dinero(sym, p.cobrado)}</span><span className="tp-num">{dinero(sym, p.regalado)}</span>
									<Barra valor={p.cobrado} maximo={maxPromo} />
								</li>
							))}
						</ul>
					</div>
					{todo && b.porEquipo.length ? <PorEquipo b={b} /> : (
						<div className="tp-card">
							<h5>Asesoras</h5>
							<Cabecera primera="Asesora" conFallidas={conFA} />
							<ul className="tp-lista">
								{b.porAsesora.map((a, i) => (
									<li key={"pa" + i} className={"tp-fila" + (conFA ? " tp-f5" : "")}>
										<span className="tp-nombre">{a.name}</span><span className="tp-num">{entero(a.aplicadas)}</span>
										<span className="tp-num">{dinero(sym, a.cobrado)}</span><span className="tp-num">{dinero(sym, a.regalado)}</span>
										{conFA ? <CeldaFallidas f={a.fallidas} sym={sym} /> : null}
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
			)}
		</>
	);
};

export default function TableroPromociones({ campusId, month, scope }: { campusId: number; month: string; scope: "campus" | "country" }) {
	const [datos, setDatos] = useState<Resumen | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let vigente = true;
		setDatos(null); setError(null);
		HttpManager.getInstance().promoApplicationsService.summary(campusId, month, scope)
			.then((r: any) => { if (vigente) setDatos(r.data); })
			.catch((e: any) => { if (vigente) setError(errorText(e)); });
		return () => { vigente = false; };
	}, [campusId, month, scope]);

	if (error) return <div className="tp"><div className="tp-vacio">{error}</div></div>;
	if (!datos) return <div className="tp"><div className="tp-carga" /></div>;
	if (!datos.visible) return null;
	const bloques: Bloque[] = datos.porMoneda?.length ? datos.porMoneda : [datos as Bloque];
	return (
		<section className="tp" aria-label="Promociones">
			<div className="tp-cab"><h4>Promociones</h4></div>
			{bloques.map((b, i) => (
				<PanelBloque key={"b" + i} b={b} todo={datos.scope.all} equipo={datos.scope.all ? null : datos.scope.team} conMoneda={bloques.length > 1} />
			))}
		</section>
	);
}
